/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import Datatable from '@/components/common/Datatable';
import {
  Box,
  Divider,
  HStack,
  Icon,
  Image,
  Spinner,
  Text,
  VStack,
  Tooltip,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import useCustomToast from '@/components/common/Toast';
import useErrorHandler from '@/utils/errorHandler';
import { Column } from 'react-table';
import { useApproveRejectReturn, useGetReturnProducts } from '@/services/returnOrders';
import { formatDateString } from '@/utils/formatDateString';
import { IReturnProduct } from '@/services/returnOrders/types';
import TickIcon from '../../../assets/svg/tick-circle.svg?react';
import CrossIcon from '../../../assets/svg/cross-circle.svg?react';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { useDisclosure } from '@chakra-ui/react';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';

interface IReturnInitiatedProps {
  updateCount: (count: number) => void;
}

const ReturnInitiated: React.FC<IReturnInitiatedProps> = ({ updateCount }) => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const [query, setQuery] = useState<string>('');
  const [filters, setFilters] = useState<{
    start_date?: string;
    end_date?: string;
    category?: string;
    subcategory?: string;
  }>({
    start_date: '',
    end_date: '',
    category: '',
    subcategory: '',
  });

  const { showToast } = useCustomToast();
  const { handleError } = useErrorHandler();

  const {
    data: Products,
    refetch,
    isLoading,
    isError,
  } = useGetReturnProducts({
    status: 'initiated',
    page: pagination.page,
    query,
    ...filters,
  });

  const { mutateAsync: approveRejectReturn } = useApproveRejectReturn();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    id: string;
    action: 'approve' | 'reject' | null;
  }>({ id: '', action: null });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleConfirmAction = async () => {
    if (!confirmAction.id || !confirmAction.action) return;

    setLoadingId(confirmAction.id);
    try {
      await approveRejectReturn({
        id: confirmAction.id,
        has_approved: confirmAction.action === 'approve',
        rejection_reason: confirmAction.action === 'reject' ? "Order can't be returned." : '',
      });
      refetch();
      showToast(
        'success',
        `Return ${confirmAction.action === 'approve' ? 'Approved' : 'Rejected'} Successfully.`,
      );
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        handleError(error);
      }
    } finally {
      setLoadingId(null);
      setConfirmAction({ id: '', action: null });
      onClose();
    }
  };

  const handleApprove = (id: string) => {
    setConfirmAction({ id, action: 'approve' });
    onOpen();
  };

  const handleReject = (id: string) => {
    setConfirmAction({ id, action: 'reject' });
    onOpen();
  };

  useEffect(() => {
    if (Products) {
      setPagination((prev) => ({
        ...prev,
        pageSize: 10,
        totalCount: Products.count,
        totalPages: Products.page_count,
      }));
      updateCount(Products.count);
    }
  }, [Products, updateCount]);

  // Cleanup to prevent tooltip persistence
  useEffect(() => {
    return () => {
      setLoadingId(null);
      setConfirmAction({ id: '', action: null });
    };
  }, []);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const applyFilters = () => refetch();

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category: '',
      subcategory: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    if (
      filters.start_date === '' &&
      filters.end_date === '' &&
      filters.category === '' &&
      filters.subcategory === ''
    ) {
      refetch();
    }
  }, [filters, pagination.page, refetch]);

  const columns: Column<IReturnProduct>[] = useMemo(
    () => [
      {
        Header: 'Order ID',
        accessor: (row) => row.order_tracking.order_id,
      },
      {
        Header: 'Product Name',
        accessor: (row) => row.order_tracking?.product_detail?.name ?? 'N/A',
        Cell: ({ row }: any) => (
          <HStack gap={2} alignItems="center" minWidth="200px">
            <Image
              h="48px"
              w="48px"
              minW="48px"
              maxW="48px"
              maxH="48px"
              borderRadius="lg"
              src={
                row.original.order_tracking?.product_detail?.images?.[0]?.image_small ??
                'default-image-url'
              }
              alt={row.original.order_tracking?.product_detail?.name ?? 'No Product'}
              objectFit="cover"
              objectPosition="top"
            />
            <VStack gap={1} align="start" justifyContent="end" h="full">
              <Text>{row.original.order_tracking?.product_detail?.name ?? 'No Product'}</Text>
            </VStack>
          </HStack>
        ),
      },
      {
        Header: 'Return Address',
        Cell: ({ row }: any) => {
          const pickupAddress = row.original.order_tracking.ship_to;
          if (!pickupAddress) {
            return <Divider orientation="horizontal" width="20px" />;
          }

          const addressParts = [
            pickupAddress.address_line_1,
            pickupAddress.landmark,
            pickupAddress.town_city,
            pickupAddress.state,
            pickupAddress.pincode,
            pickupAddress.country,
          ].filter(Boolean);

          return <Text minWidth="200px">{addressParts.join(', ')}</Text>;
        },
      },
      {
        Header: 'Refund Amount',
        accessor: (row) => row.refund_amount || '',
        Cell: ({ value, row }: any) =>
          value ? (
            <Text>{`${getCurrencySymbol(row.original.order_tracking.currency)}${value.toFixed(2)}`}</Text>
          ) : (
            <Divider orientation="horizontal" width="20px" />
          ),
      },
      {
        Header: 'Payment',
        accessor: (row) => row.order_tracking?.transaction?.payment_method ?? 'N/A',
      },
      {
        Header: 'Order Date',
        Cell: ({ row }: any) => {
          const orderDate = row.original.order_tracking?.created_at
            ? formatDateString(row.original.order_tracking.created_at)
            : 'N/A';
          return <Text minWidth="150px">{orderDate}</Text>;
        },
      },
      {
        Header: 'Return Requested',
        Cell: ({ row }: any) => {
          const returnDate = formatDateString(row.original.returned_at);
          return <Text minWidth="150px">{returnDate}</Text>;
        },
      },
      {
        Header: 'Reason',
        accessor: (row) => row.reason.reason,
        Cell: ({ value }: any) => <Text minWidth="200px">{value}</Text>,
      },
      {
        Header: 'Actions',
        Cell: ({ row }: any) => (
          <HStack gap={2} key={row.original.id}>
            {loadingId === row.original.id ? (
              <Spinner size="sm" color="brand.primary.default" />
            ) : (
              <>
                <Tooltip
                  label="Approve Return"
                  hasArrow
                  placement="bottom"
                  shouldWrapChildren
                  closeOnClick
                  zIndex={1500}
                >
                  <Icon
                    as={TickIcon}
                    boxSize={5}
                    cursor="pointer"
                    onClick={() => handleApprove(row.original.id)}
                  />
                </Tooltip>
                <Tooltip
                  label="Reject Return"
                  hasArrow
                  placement="bottom"
                  shouldWrapChildren
                  closeOnClick
                  zIndex={1500}
                >
                  <Icon
                    as={CrossIcon}
                    boxSize={5}
                    cursor="pointer"
                    onClick={() => handleReject(row.original.id)}
                  />
                </Tooltip>
              </>
            )}
          </HStack>
        ),
      },
    ],
    [Products, loadingId],
  );

  return (
    <Box bg="background.white">
      <Datatable
        data={Products?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <CustomConfirmationModal
        isOpen={isOpen}
        onClose={() => {
          setConfirmAction({ id: '', action: null });
          onClose();
        }}
        onConfirm={handleConfirmAction}
        icon={confirmAction.action === 'approve' ? TickIcon : CrossIcon}
        title={
          confirmAction.action === 'approve' ? 'Confirm Approve Return' : 'Confirm Reject Return'
        }
        description={
          confirmAction.action === 'approve'
            ? 'Are you sure you want to approve this return? This action will proceed with the return process.'
            : 'Are you sure you want to reject this return? This action cannot be undone.'
        }
        buttonText={confirmAction.action === 'approve' ? 'Approve' : 'Reject'}
        isLoading={loadingId !== null}
      />
    </Box>
  );
};

export default ReturnInitiated;
/* eslint-disable @typescript-eslint/no-explicit-any */
